/**
 * Terms and Conditions Offline Cache Service
 * Handles caching T&C content for offline viewing
 * Follows the same patterns as offlineCache.ts
 */

import { storage } from './storage';
import { TCContent, TCStatus, AcceptanceInfo } from '../types/termsConditions';

// Storage keys for T&C caching
const TC_STORAGE_KEYS = {
  CURRENT_TC: 'tc_cached_current',
  TC_STATUS: 'tc_cached_status',
  ACCEPTANCE_INFO: 'tc_cached_acceptance_info',
  LAST_TC_SYNC: 'tc_last_sync',
} as const;

interface CachedTCContent {
  data: TCContent;
  cachedAt: number;
  version: string;
}

interface CachedTCStatus {
  data: TCStatus;
  cachedAt: number;
}

interface CachedAcceptanceInfo {
  data: AcceptanceInfo;
  cachedAt: number;
}

/**
 * T&C Offline Cache Service
 * Provides offline access to Terms and Conditions content
 */
class TCOfflineCacheService {
  /**
   * Cache the current T&C content
   */
  async cacheCurrentTC(tcContent: TCContent): Promise<void> {
    if (!tcContent || !tcContent.version) {
      console.warn('Skipping T&C cache: content or version is null/undefined');
      return;
    }
    try {
      const cached: CachedTCContent = {
        data: tcContent,
        cachedAt: Date.now(),
        version: tcContent.version,
      };
      await storage.setJSON(TC_STORAGE_KEYS.CURRENT_TC, cached);
      console.log(`✅ Cached T&C version ${tcContent.version} for offline use`);
    } catch (error) {
      console.error('Error caching T&C content:', error);
    }
  }

  /**
   * Get cached T&C content
   * Returns null if no cached content exists
   */
  async getCachedCurrentTC(): Promise<TCContent | null> {
    try {
      const cached = await storage.getJSON<CachedTCContent>(TC_STORAGE_KEYS.CURRENT_TC);
      if (cached?.data) {
        console.log(`✅ Retrieved cached T&C version ${cached.version} (cached ${this.formatAge(cached.cachedAt)})`);
        return cached.data;
      }
      return null;
    } catch (error) {
      console.error('Error getting cached T&C:', error);
      return null;
    }
  }

  /**
   * Cache T&C status
   */
  async cacheTCStatus(status: TCStatus): Promise<void> {
    try {
      const cached: CachedTCStatus = {
        data: status,
        cachedAt: Date.now(),
      };
      await storage.setJSON(TC_STORAGE_KEYS.TC_STATUS, cached);
      console.log('✅ Cached T&C status for offline use');
    } catch (error) {
      console.error('Error caching T&C status:', error);
    }
  }

  /**
   * Get cached T&C status
   */
  async getCachedTCStatus(): Promise<TCStatus | null> {
    try {
      const cached = await storage.getJSON<CachedTCStatus>(TC_STORAGE_KEYS.TC_STATUS);
      if (cached?.data) {
        console.log(`✅ Retrieved cached T&C status (cached ${this.formatAge(cached.cachedAt)})`);
        return cached.data;
      }
      return null;
    } catch (error) {
      console.error('Error getting cached T&C status:', error);
      return null;
    }
  }

  /**
   * Cache acceptance info
   */
  async cacheAcceptanceInfo(info: AcceptanceInfo): Promise<void> {
    try {
      const cached: CachedAcceptanceInfo = {
        data: info,
        cachedAt: Date.now(),
      };
      await storage.setJSON(TC_STORAGE_KEYS.ACCEPTANCE_INFO, cached);
      console.log('✅ Cached T&C acceptance info for offline use');
    } catch (error) {
      console.error('Error caching acceptance info:', error);
    }
  }

  /**
   * Get cached acceptance info
   */
  async getCachedAcceptanceInfo(): Promise<AcceptanceInfo | null> {
    try {
      const cached = await storage.getJSON<CachedAcceptanceInfo>(TC_STORAGE_KEYS.ACCEPTANCE_INFO);
      if (cached?.data) {
        console.log(`✅ Retrieved cached acceptance info (cached ${this.formatAge(cached.cachedAt)})`);
        return cached.data;
      }
      return null;
    } catch (error) {
      console.error('Error getting cached acceptance info:', error);
      return null;
    }
  }

  /**
   * Check if cached T&C version matches a specific version
   */
  async isCachedVersionCurrent(version: string): Promise<boolean> {
    try {
      const cached = await storage.getJSON<CachedTCContent>(TC_STORAGE_KEYS.CURRENT_TC);
      return cached?.version === version;
    } catch {
      return false;
    }
  }

  /**
   * Get cache metadata for debugging/display
   */
  async getCacheInfo(): Promise<{
    hasCurrentTC: boolean;
    cachedVersion: string | null;
    cachedAt: number | null;
    lastSync: number | null;
  }> {
    try {
      const cached = await storage.getJSON<CachedTCContent>(TC_STORAGE_KEYS.CURRENT_TC);
      const lastSync = await storage.getJSON<{ timestamp: number }>(TC_STORAGE_KEYS.LAST_TC_SYNC);

      return {
        hasCurrentTC: !!cached?.data,
        cachedVersion: cached?.version || null,
        cachedAt: cached?.cachedAt || null,
        lastSync: lastSync?.timestamp || null,
      };
    } catch (error) {
      console.error('Error getting cache info:', error);
      return {
        hasCurrentTC: false,
        cachedVersion: null,
        cachedAt: null,
        lastSync: null,
      };
    }
  }

  /**
   * Set last sync time
   */
  async setLastSyncTime(timestamp: number = Date.now()): Promise<void> {
    try {
      await storage.setJSON(TC_STORAGE_KEYS.LAST_TC_SYNC, { timestamp });
    } catch (error) {
      console.error('Error setting T&C last sync time:', error);
    }
  }

  /**
   * Get last sync time
   */
  async getLastSyncTime(): Promise<number | null> {
    try {
      const syncData = await storage.getJSON<{ timestamp: number }>(TC_STORAGE_KEYS.LAST_TC_SYNC);
      return syncData?.timestamp || null;
    } catch (error) {
      console.error('Error getting T&C last sync time:', error);
      return null;
    }
  }

  /**
   * Clear all T&C cached data
   */
  async clearCache(): Promise<void> {
    try {
      await Promise.all([
        storage.removeItem(TC_STORAGE_KEYS.CURRENT_TC),
        storage.removeItem(TC_STORAGE_KEYS.TC_STATUS),
        storage.removeItem(TC_STORAGE_KEYS.ACCEPTANCE_INFO),
        storage.removeItem(TC_STORAGE_KEYS.LAST_TC_SYNC),
      ]);
      console.log('✅ T&C offline cache cleared');
    } catch (error) {
      console.error('Error clearing T&C cache:', error);
    }
  }

  /**
   * Update cached status when user accepts T&C locally
   * This ensures the UI shows the correct state even before syncing
   */
  async updateStatusAfterAcceptance(tcId: number, version: string): Promise<void> {
    try {
      const now = new Date().toISOString();

      // Update cached status
      const newStatus: TCStatus = {
        requiresAcceptance: false,
        currentVersion: version,
        acceptedVersion: version,
        acceptedAt: now,
        acceptedOnPlatform: null, // Will be set by server
      };
      await this.cacheTCStatus(newStatus);

      // Update cached acceptance info
      const newAcceptanceInfo: AcceptanceInfo = {
        hasAccepted: true,
        acceptedAt: now,
        version: version,
        acceptedOnPlatform: null, // Will be set by server
      };
      await this.cacheAcceptanceInfo(newAcceptanceInfo);

      console.log(`✅ Updated cached T&C status after acceptance of version ${version}`);
    } catch (error) {
      console.error('Error updating cached status after acceptance:', error);
    }
  }

  /**
   * Format cache age for logging
   */
  private formatAge(timestamp: number): string {
    const ageMs = Date.now() - timestamp;
    const ageMinutes = Math.floor(ageMs / 60000);
    const ageHours = Math.floor(ageMinutes / 60);

    if (ageHours > 0) {
      return `${ageHours}h ${ageMinutes % 60}m ago`;
    }
    return `${ageMinutes}m ago`;
  }
}

export const tcOfflineCache = new TCOfflineCacheService();
